import React from "react";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import ArrowDropUpIcon from "@material-ui/icons/ArrowDropUp";
import { ASC, DESC, UNAVAILABILITY_MESSAGE, FIELDS_TO_FILTER } from "./data";
import moment from "moment";
import renderField from "../components/PropertiesList/renderer/index.jsx";
import TableCell from "@material-ui/core/TableCell";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import Visibility from "@material-ui/icons/Visibility";
import ToggleOn from "@material-ui/icons/ToggleOn";
import ToggleOff from "@material-ui/icons/ToggleOff";
import Edit from "@material-ui/icons/Edit";
import RemoveCircle from "@material-ui/icons/RemoveCircle";
import DoneIcon from "@material-ui/icons/Done";
import ListAltIcon from "@material-ui/icons/ListAlt";
import { Checkbox, ThemeProvider } from "@material-ui/core";
import Button from "@material-ui/core/Button";
import { Link } from "react-router-dom";
import {
    viewButtonTheme,
    editButtonTheme,
    toggleButtonTheme,
    removeButtonTheme,
    loadExcelButtonTheme,
    selectHotelButtonTheme,
} from "../config/theme";
import styles from "./helperFuncs.module.css";

/**
 * Format summ according to currency type
 *
 * @param {Number} num
 */

export const formatMoney = (num) => {
    let numToShow = num || 0;

    return new Intl.NumberFormat("ru-RU", {
        style: "currency",
        currency: "RUB",
        // minimumFractionDigits: 0,
        // maximumFractionDigits: 0
    }).format(numToShow);
};
/**
 * Format date using moment.js
 *
 * @param {String | Number} date
 */

export const formatDate = (date) => {
    if (!date) return UNAVAILABILITY_MESSAGE;

    if (isNaN(date)) {
        return moment(date).format("DD/MM/YYYY HH:mm:ss");
    } else if (!isNaN(date)) {
        return moment.unix(date).format("DD/MM/YYYY HH:mm:ss");
    }
};

/**
 * Format guest name
 *
 * @param {Object} guest
 */

export const formatGuestName = (guest) => {
    if (!guest) return;

    const lastName = guest.last_name ? guest.last_name : "";
    const firstName = guest.first_name ? guest.first_name : "";
    const middleName = guest.middle_name ? guest.middle_name : "";
    const guestName = `${lastName} ${firstName} ${middleName}`;

    return guestName;
};

/**
 * Format status showing its name and not just type
 *
 * @param {String} sts
 * @param {Array} fields
 */

export const formatStatus = (sts, fields) =>
    renderField({ ...fields["status"], value: sts }).props.children;

/**
 * Get day of the week num
 *
 * @param {moment} date
 */

export const getWeekDayNum = (date) => {
    if (!date) return;
    if (moment(date).day() === -1) return 6;

    return moment(date).day();
};

/**
 * Get day of the week name from array
 *
 * @param {Number} weekDayNum
 * @param {Array} weekDays
 */
export const showWeekDay = (weekDayNum, weekDays) => weekDays[weekDayNum];

/**
 * Renders ort order arrow if needed
 *
 * @param {String} sortField
 * @param {String} sortOrder
 * @param {String} columnKey
 */

export const renderSortArrow = (sortField, sortOrder, columnKey) => {
    if (!sortField) return null;

    let arrowIcon;
    if (sortField === columnKey) {
        switch (sortOrder) {
            case ASC:
                arrowIcon = <ArrowDropUpIcon />;
                break;
            case DESC:
                arrowIcon = <ArrowDropDownIcon />;
        }
    }

    return (
        <span
            style={{
                verticalAlign: "middle",
            }}
        >
            {arrowIcon}
        </span>
    );
};

/**
 *
 * Renders button headers
 *
 * @param {Object} buttonActions
 */

export const buttonHeaders = (
    buttonActions,
    selectedIds,
    allIds,
    setSelectedIds,
    handleMassAction,
    massActionsPartitions,
    massActionButtons,
) => {
    const isAllSelected =
        allIds?.length > 0 && selectedIds?.length === allIds?.length;

    const handleToggleAll = () => {
        setSelectedIds(isAllSelected ? [] : [...allIds]);
    };

    return [
        massActionsPartitions?.includes(buttonActions?.partition) && (
            <TableCell key="header_select_all">
                <div className={styles.container__all}>
                    <Checkbox
                        checked={isAllSelected}
                        indeterminate={
                            selectedIds.length > 0 &&
                            selectedIds.length < allIds.length
                        }
                        onChange={handleToggleAll}
                    />

                    {selectedIds.length > 0 && (
                        <div className={styles.bulkActions}>
                            {massActionButtons}
                        </div>
                    )}
                </div>
            </TableCell>
        ),
        buttonActions.viewAction && !buttonActions?.thisManteraExport && (
            <TableCell align="center" key="header_view">
                Управление
            </TableCell>
        ),
        buttonActions.loadExcelAction && (
            <TableCell align="right" key="header_load_excel">
                Excel
            </TableCell>
        ),
        buttonActions.selectHotelAction && (
            <TableCell align="right" key="header_select_hotel">
                Выбрать
            </TableCell>
        ),
    ];
};

/**
 *
 * Add buttons to the table according functions it has
 *
 * @param {Object} props
 */

export const addButtons = (
    props,
    selectedIds,
    setSelectedIds,
    massActionsPartitions,
) => {
    const isSelected = selectedIds?.includes(props.id);

    const toggleSelection = () => {
        if (isSelected) {
            setSelectedIds(selectedIds.filter((id) => id !== props.id));
        } else {
            setSelectedIds([...selectedIds, props.id]);
        }
    };

    const buttons = [];

    if (massActionsPartitions?.includes(props?.partition)) {
        buttons.push(
            <TableCell key={`select_${props.id}`} align="left">
                <Checkbox checked={isSelected} onChange={toggleSelection} />
            </TableCell>,
        );
    }

    if (props.viewAction && !props?.thisManteraExport) {
        buttons.push(
            <TableCell
                className={styles.control}
                align="center"
                key={`view_${props.id}`}
            >
                {props.needViewCopyButton && (
                    <ThemeProvider theme={viewButtonTheme}>
                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<FileCopyIcon />}
                            onClick={() => props.copyObject(props.id)}
                        >
                            {""}
                        </Button>
                    </ThemeProvider>
                )}
                {props.editAction && (
                    <ThemeProvider theme={editButtonTheme}>
                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<Edit />}
                            component={Link}
                            to={props.editAction(props.id)}
                        >
                            {""}
                        </Button>
                    </ThemeProvider>
                )}

                {/*<ThemeProvider theme={viewButtonTheme}>*/}
                {/*    <Button*/}
                {/*        variant="outlined"*/}
                {/*        color="primary"*/}
                {/*        startIcon={<Visibility />}*/}
                {/*        to={props.viewAction(props.id)}*/}
                {/*        component={Link}*/}
                {/*    >*/}
                {/*        {""}*/}
                {/*    </Button>*/}
                {/*</ThemeProvider>*/}
                {!props?.thisPurchasedServices && props.toggleAction && (
                    <ThemeProvider theme={toggleButtonTheme}>
                        <Button
                            role="toggle"
                            onClick={() => {
                                props.toggleAction(props.id, !props.active);
                            }}
                            variant="outlined"
                            color={!props.active ? "secondary" : "primary"}
                            startIcon={
                                props.active ? <ToggleOff /> : <ToggleOn />
                            }
                        >
                            {""}
                        </Button>
                    </ThemeProvider>
                )}

                {(props.removeAction || props?.may_deleted) && (
                    <ThemeProvider theme={removeButtonTheme}>
                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<RemoveCircle />}
                            onClick={() =>
                                props.removeAction(props.id, props.name)
                            }
                        >
                            {""}
                        </Button>
                    </ThemeProvider>
                )}
            </TableCell>,
        );
    }

    if (props.loadExcelAction) {
        buttons.push(
            <TableCell align="right" key={`load_excel_${props.id}`}>
                <ThemeProvider theme={loadExcelButtonTheme}>
                    <Button
                        variant="outlined"
                        color="primary"
                        startIcon={<ListAltIcon />}
                        component={Link}
                        to={props.loadExcelAction(props.id)}
                    >
                        {""}
                    </Button>
                </ThemeProvider>
            </TableCell>,
        );
    }

    if (props?.item?.unique_quantity != null) {
        buttons.push(
            <TableCell align="right" key={`load_excel_${props.id}`}>
                <ThemeProvider theme={loadExcelButtonTheme}>
                    <Button
                        variant="outlined"
                        color="primary"
                        startIcon={<ListAltIcon />}
                        onClick={() =>
                            window.open(
                                "/manager/data/unique_promo_codes/download/" +
                                    props.id,
                            )
                        }
                    >
                        {""}
                    </Button>
                </ThemeProvider>
            </TableCell>,
        );
    }
    if (props.selectHotelAction) {
        buttons.push(
            <TableCell align="right" key={`remove_${props.id}`}>
                <ThemeProvider theme={selectHotelButtonTheme}>
                    <Button
                        variant="outlined"
                        color="primary"
                        startIcon={<DoneIcon />}
                        onClick={() => {
                            props.selectHotelAction(props.item);
                            props.setDirectLink(false);
                        }}
                    >
                        {""}
                    </Button>
                </ThemeProvider>
            </TableCell>,
        );
    }
    return buttons;
};

/**
 * Checks if items were filtered and returns value accordingly
 *
 * @param {boolean} filteredWithString
 * @param {boolean} filteredWithDate
 * @param {boolean} filteredWithBoolean
 * @param {boolean} filteredWithArray
 * @param {Array.<Object>} defaultItems
 * @param {Array.<Object>} filteredItems
 */

export const getArrToFilter = (
    filteredWithString,
    filteredWithDate,
    filteredWithBoolean,
    filteredWithArray,
    defaultItems,
    filteredItems,
) =>
    !filteredWithString &&
    !filteredWithDate &&
    !filteredWithBoolean &&
    !filteredWithArray
        ? defaultItems
        : filteredItems;

export const stripTags = (string) => {
    return string.replace(/(<([^>]+)>)/gi, "");
};

export const createFormData = (item) => {
    const formDataToSend = new FormData();

    for (let key in item) {
        if (
            (!(key === "iconn" && typeof item[key] === "string") &&
                item[key]) ||
            item[key] === false
        ) {
            if (item[key] === true) {
                formDataToSend.append(key, 1);
                continue;
            } else if (item[key] === false) {
                formDataToSend.append(key, 0);
                continue;
            }

            formDataToSend.append(key, item[key]);
        }
    }

    return formDataToSend;
};

export const createAccObjFromStr = (accStr) => {
    if (accStr) {
        const accObj = {};
        const auxValue = accStr.split(".");
        accObj.adults = auxValue[0];
        accObj.children = auxValue[1];
        accObj.babies = auxValue[2];
        accObj.extra_bed = auxValue[3];
        accObj.craddle = auxValue[4];

        return accObj;
    }
};

export const formatAccType = (accStr) => {
    if (accStr && accStr.includes(".")) {
        const strArr = accStr.split(".");

        let extra = "";
        if (strArr[3] == 1 && strArr[4] == 0) extra = "(EB)";
        else if (strArr[3] == 0 && strArr[4] == 1) extra = "(CR)";
        else if (strArr[3] == 1 && strArr[4] == 1) extra = "(EB, CR)";

        const formattedStr = `${strArr[0]} / ${strArr[1]} + ${strArr[2]} ${extra}`;

        return formattedStr;
    }

    return accStr;
};

export const strFromAccTarget = (accTargetObj) =>
    `${accTargetObj.adults}.${accTargetObj.children}.${accTargetObj.babies}.${accTargetObj.extra_bed}.${accTargetObj.craddle}`;

// Func to turn number into str with dot to preserve object insertion order
export const turnNumbertoObjkey = (number) => number.toString() + ".";

/**
 *
 * @param {object} fields
 * @returns {object} filtered fields for entity to be shown for user in view form
 */

export const filterForView = (fields) => {
    const filteredFields = { ...fields };
    FIELDS_TO_FILTER.view.forEach(
        (field) =>
            Reflect.has(filteredFields, field) &&
            Reflect.deleteProperty(filteredFields, field),
    );

    return filteredFields;
};

/**
 *
 * @param {object} fields
 * @returns {object} filtered fields for entity to be shown for user in edit form
 */

export const filterForEdit = (fields) => {
    const filteredFields = { ...fields };
    FIELDS_TO_FILTER.edit.forEach(
        (field) =>
            Reflect.has(filteredFields, field) &&
            Reflect.deleteProperty(filteredFields, field),
    );

    return filteredFields;
};

/**
 *
 * @param {object} fields
 * @returns {object} Возвращает грид цен, где есть хотя бы одна цена, исключая пустые.
 */

export const getRealyUpdated = (updatedGrid) => {
    const realyUpdatedGrid = {};
    for (const [key, value] of Object.entries(updatedGrid)) {
        for (const [k, v] of Object.entries(value)) {
            if (v.id) {
                realyUpdatedGrid[key] = value;
                continue;
            }
        }
    }
    return realyUpdatedGrid;
};
